import React, { useState } from 'react';

import {
  Button,
  Collapse,
  Dialog,
  Typography,
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CloudUpload, GetApp, Clear, Delete, Error, Warning, ExpandMore } from '@material-ui/icons';
import { useSnackbar } from 'notistack';

import UserService from '../../services/user.service';
import { readFileAsText, parseCSV, validateUserRow } from '../../utils/csvParser';

const useStyles = makeStyles((theme) => ({
  buttonsContainer: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 24,
    width: '100%',
  },
  cancelButton: {
    width: 150,
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
      margin: '16px',
      maxWidth: '90%',
      overflow: 'visible',
    },
  },
  errorBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    color: '#856404',
    marginTop: 16,
    padding: '12px 16px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
  },
  header: {
    textAlign: 'center',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '36px 24px',
  },
  submitButton: {
    width: 150,
  },
  subtitle: {
    color: '#6B778C',
    fontSize: 14,
    marginBottom: 8,
    textAlign: 'center',
  },
  title: {
    color: '#172B4D',
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    textAlign: 'center',
  },
  uploadButton: {
    width: '100%',
  },
  uploadSection: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    minWidth: 400,
    padding: 24,
  },
}));

const ImportUsersModal = ({ open, onClose, onUsersImported }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [validatedRows, setValidatedRows] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showFieldHelp, setShowFieldHelp] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    const isCSV = selectedFile && (selectedFile.type === 'text/csv' || selectedFile.name.toLowerCase().endsWith('.csv'));

    if (isCSV) {
      setFile(selectedFile);
      setResults(null);
      setShowPreview(false);
      setLoading(true);

      try {
        const csvText = await readFileAsText(selectedFile);
        const parsed = parseCSV(csvText);

        // Check for duplicate emails and filter them out
        const emailMap = new Map();
        const duplicateEmails = [];
        const uniqueRows = [];

        parsed.rows.forEach((row, index) => {
          const email = row.email?.toLowerCase().trim();
          if (email) {
            if (emailMap.has(email)) {
              duplicateEmails.push(email);
            } else {
              emailMap.set(email, true);
              uniqueRows.push({ ...row, originalIndex: index });
            }
          } else {
            uniqueRows.push({ ...row, originalIndex: index });
          }
        });

        if (duplicateEmails.length > 0) {
          const uniqueDuplicates = [...new Set(duplicateEmails)];

          enqueueSnackbar(`Duplicate emails removed: ${uniqueDuplicates.join(', ')}`, { variant: 'warning' });
        }

        const updatedParsed = {
          ...parsed,
          rows: uniqueRows,
        };
        setParsedData(updatedParsed);

        // Do basic client-side validation first
        const basicValidated = uniqueRows.map((row, index) => {
          const validation = validateUserRow(row);
          return {
            ...row,
            _id: `row_${index}`,
            _validation: validation,
          };
        });

        // Only proceed with backend validation if basic validation passes for at least some rows
        const basicValidRows = basicValidated.filter((row) => row._validation.isValid);

        if (basicValidRows.length === 0) {
          setValidatedRows(basicValidated);
          setShowPreview(true);
          setLoading(false);

          enqueueSnackbar('No valid rows found. Please check the CSV format.', { variant: 'error' });

          return;
        }

        // Call backend for user, deparments and facilities validation
        const userService = new UserService();
        const validationResponse = await userService.validateUsersBulk(uniqueRows);

        // Map backend validation results to our format
        const validated = validationResponse.results.map((result) => {
          return {
            ...result.userData,
            _id: `row_${result.rowIndex}`,
            _validation: {
              errors: result.errors,
              isValid: result.isValid,
              warnings: result.warnings || [],
            },
          };
        });

        setValidatedRows(validated);
        setShowPreview(true);
      } catch (error) {
        console.error('Error validating CSV:', error);
        enqueueSnackbar('Error validating CSV file', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    } else {
      enqueueSnackbar('Please select a valid CSV file', { variant: 'error' });
    }
  };

  const handleImport = async () => {
    if (!validatedRows || validatedRows.length === 0) {
      enqueueSnackbar('No valid data to import', { variant: 'error' });
      return;
    }

    const validRows = validatedRows.filter((row) => row._validation.isValid);
    if (validRows.length === 0) {
      enqueueSnackbar('No valid rows to import. Please fix validation errors first.', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      // Prepare user data for import
      const usersToImport = validRows.map((row) => {
        // Remove internal fields and return clean user data
        const { _id, _validation, ...userData } = row;
        return userData;
      });

      const userService = new UserService();
      const result = await userService.importUsersData(usersToImport);

      setResults(result);
      setShowPreview(false);

      if (result.successful > 0) {
        enqueueSnackbar(`Successfully imported ${result.successful} users`, { variant: 'success' });
        onUsersImported();
      }

      if (result.failed > 0) {
        const failedUserNames = result.errors?.map((error) => error.data?.name || `Row ${error.row}`).join(', ') || 'Unknown users';
        enqueueSnackbar(`${result.failed} users failed to import: ${failedUserNames}`, { variant: 'warning' });
      }

      handleClose();
    } catch (error) {
      console.error('Error importing users:', error);
      enqueueSnackbar('Failed to import users', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    setParsedData(null);
    setValidatedRows([]);
    setShowPreview(false);
    setLoading(false);
    onClose();
  };

  const removeRow = (rowId) => {
    setValidatedRows((prev) => {
      const newRows = prev.filter((row) => row._id !== rowId);
      // If no rows left, reset everything
      if (newRows.length === 0) {
        setShowPreview(false);
        setFile(null);
        setParsedData(null);
        // Reset the file input
        const fileInput = document.getElementById('csv-file-input');
        if (fileInput) {
          fileInput.value = '';
        }
      }
      return newRows;
    });
  };

  const clearAllRows = () => {
    setValidatedRows([]);
    setShowPreview(false);
    setFile(null);
    setParsedData(null);
    // Reset the file input
    const fileInput = document.getElementById('csv-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const getValidRowCount = () => {
    return validatedRows.filter((row) => row._validation.isValid).length;
  };

  const getInvalidRowCount = () => {
    return validatedRows.filter((row) => !row._validation.isValid).length;
  };

  const renderValidationStatus = (row) => {
    if (!row._validation.isValid) {
      return (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Chip label="Invalid" color="secondary" size="small" />
          <Tooltip
            title={
              <Box>
                <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  Validation Errors:
                </Typography>
                {row._validation.errors.map((error) => (
                  <Typography key={`error-${error}`} variant="body2" style={{ marginBottom: 2 }}>
                    • {error}
                  </Typography>
                ))}
              </Box>
            }>
            <Error color="error" fontSize="small" style={{ cursor: 'help' }} />
          </Tooltip>
        </Box>
      );
    }

    if (row._validation.warnings && row._validation.warnings.length > 0) {
      return (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Chip label="Valid*" color="primary" size="small" variant="outlined" />
          <Tooltip
            title={
              <Box>
                <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  Warnings:
                </Typography>
                {row._validation.warnings.map((warning) => (
                  <Typography key={`warning-${warning}`} variant="body2" style={{ marginBottom: 2 }}>
                    • {warning}
                  </Typography>
                ))}
              </Box>
            }>
            <Warning color="primary" fontSize="small" style={{ cursor: 'help' }} />
          </Tooltip>
        </Box>
      );
    }

    return <Chip label="Valid" color="primary" size="small" />;
  };

  const downloadTemplate = () => {
    const csvContent =
      'name,email,role,facilityId,departmentId,phoneNumber,fullTimeEmployment,modality\n' +
      'John Doe,john.doe@example.com,technologist,1,1,+1234567890,1,ct\n' +
      'Jane Smith,jane.smith@example.com,radiologist,1,2,+1987654321,1,';

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog}>
      <div className={classes.modalContent}>
        <div className={classes.container}>
          <div className={classes.header}>
            <Typography variant="h4" className={classes.title}>
              Import Users
            </Typography>

            {!results && (
              <Typography variant="body2" className={classes.subtitle}>
                Upload a CSV file to import multiple users at once
              </Typography>
            )}
          </div>

          <div className={classes.formContainer}>
            {!showPreview && !results && (
              <Box className={classes.uploadSection}>
                <Typography variant="body1" gutterBottom style={{ marginBottom: 8 }}>
                  The file should contain the following columns:
                </Typography>

                <Typography variant="body2" component="div">
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold' }}>Required:</span> name, email, role
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold' }}>Optional:</span> facilityId, departmentId, phoneNumber, fte, modality
                  </div>

                  <Button onClick={() => setShowFieldHelp(!showFieldHelp)} size="small" style={{ marginBottom: 8, padding: '2px 8px', textTransform: 'none' }}>
                    <ExpandMore
                      style={{
                        fontSize: 18,
                        marginRight: 4,
                        transform: showFieldHelp ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                      }}
                    />
                    {showFieldHelp ? 'Hide' : 'Show'} field descriptions
                  </Button>

                  <Collapse in={showFieldHelp}>
                    <Box style={{ backgroundColor: '#f5f5f5', borderRadius: 4, marginBottom: 12, padding: 12 }}>
                      <Typography variant="body2" style={{ marginBottom: 8 }}>
                        <strong>facilityId:</strong> Numeric ID of the facility
                      </Typography>
                      <Typography variant="body2" style={{ marginBottom: 8 }}>
                        <strong>departmentId:</strong> Numeric ID of the department (requires facilityId)
                      </Typography>
                      <Typography variant="body2" style={{ marginBottom: 8 }}>
                        <strong>fullTimeEmployment:</strong> Full Time Employment ratio between 0 and 1 (default: 1)
                      </Typography>
                      <Typography variant="body2" style={{ marginBottom: 8 }}>
                        <strong>modality:</strong> Required for technologist role only. Valid values:
                      </Typography>
                      <Box style={{ marginLeft: 16, marginTop: 4 }}>
                        <Chip label="bone_densitometry" size="small" style={{ margin: 2, padding: 4 }} />
                        <Chip label="ct" size="small" style={{ margin: 2, padding: 4 }} />
                        <Chip label="fluoroscopy" size="small" style={{ margin: 2, padding: 4 }} />
                        <Chip label="mammography" size="small" style={{ margin: 2, padding: 4 }} />
                        <Chip label="mri" size="small" style={{ margin: 2, padding: 4 }} />
                        <Chip label="nuclear_medicine" size="small" style={{ margin: 2, padding: 4 }} />
                        <Chip label="us" size="small" style={{ margin: 2, padding: 4 }} />
                        <Chip label="xray" size="small" style={{ margin: 2, padding: 4 }} />
                      </Box>
                    </Box>
                  </Collapse>

                  <Button variant="outlined" startIcon={<GetApp />} onClick={downloadTemplate} size="small" className="my-1">
                    Download Template
                  </Button>
                </Typography>

                <input accept=".csv" style={{ display: 'none' }} id="csv-file-input" type="file" onChange={handleFileChange} />
                <label htmlFor="csv-file-input">
                  <Button variant="contained" component="span" startIcon={<CloudUpload />} className={classes.uploadButton} color="primary">
                    {file ? file.name : 'Select CSV File'}
                  </Button>
                </label>
              </Box>
            )}

            {loading && (
              <Box mb={2}>
                <LinearProgress />
                <Typography variant="body2" align="center" style={{ marginTop: 8 }}>
                  {showPreview ? 'Importing users...' : 'Validating CSV data...'}
                </Typography>
              </Box>
            )}

            {showPreview && validatedRows.length > 0 && (
              <Box className={classes.previewSection}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Data Preview</Typography>

                  <Button variant="outlined" color="secondary" startIcon={<Clear />} onClick={clearAllRows} size="small">
                    Clear All
                  </Button>
                </Box>

                <TableContainer component={Paper} style={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Actions</TableCell>
                        <TableCell>Status</TableCell>
                        {parsedData.headers.map((header) => (
                          <TableCell key={header}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validatedRows.map((row) => (
                        <TableRow
                          key={row._id}
                          style={{
                            backgroundColor: row._validation.isValid ? 'transparent' : '#ffebee',
                          }}>
                          <TableCell>
                            <Tooltip title="Remove row">
                              <IconButton size="small" onClick={() => removeRow(row._id)} color="secondary">
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{renderValidationStatus(row)}</TableCell>
                          {parsedData.headers.map((header) => (
                            <TableCell key={header}>{row[header] || ''}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {getInvalidRowCount() > 0 && (
                  <Box className={classes.errorBox}>
                    <Typography variant="body2">⚠️ {getInvalidRowCount()} rows have validation errors. Only valid rows will be imported.</Typography>
                  </Box>
                )}
              </Box>
            )}
          </div>

          <div className={classes.buttonsContainer}>
            <Button variant="outlined" color="secondary" onClick={handleClose} className={classes.cancelButton} disabled={loading}>
              {results ? 'Close' : 'Cancel'}
            </Button>

            {!results && showPreview && getValidRowCount() > 0 && (
              <Button onClick={handleImport} variant="contained" color="primary" className={classes.submitButton} disabled={loading}>
                {`Import ${getValidRowCount()} User${getValidRowCount() === 1 ? '' : 's'}`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ImportUsersModal;
